// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type Metric } from '@shared/data-access/interfaces/api/ai/esg-analysis';
import { ReactComponent as AdultContent } from '@shared/ui-kit/assets/icons/exposure/adult-content.svg';
import { ReactComponent as Alcohol } from '@shared/ui-kit/assets/icons/exposure/alcohol.svg';
import { ReactComponent as Biohazard } from '@shared/ui-kit/assets/icons/exposure/biohazard.svg';
import { ReactComponent as Boot } from '@shared/ui-kit/assets/icons/exposure/boot.svg';
import { ReactComponent as Coal } from '@shared/ui-kit/assets/icons/exposure/coal.svg';
import { ReactComponent as Gambling } from '@shared/ui-kit/assets/icons/exposure/gambling.svg';
import { ReactComponent as Genetics } from '@shared/ui-kit/assets/icons/exposure/genetics.svg';
import { ReactComponent as Nuclear } from '@shared/ui-kit/assets/icons/exposure/nuclear.svg';
import { ReactComponent as Rabbit } from '@shared/ui-kit/assets/icons/exposure/rabbit.svg';
import { ReactComponent as Tobacco } from '@shared/ui-kit/assets/icons/exposure/tobacco.svg';
import { ReactComponent as Firearms } from '@shared/ui-kit/assets/icons/exposure/firearms.svg';

import { ChartContainer } from '@shared/ui-kit/components/charts/chart-container';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface SectorsProps {
  metrics: Omit<Metric, 'not_informed_pct'>[];
}

const METRIC_ICONS = {
  adult_content: <AdultContent data-testid="adult-content" />,
  alcoholic: <Alcohol data-testid="alcoholic" />,
  animal_testing: <Rabbit />,
  coal_energy: <Coal />,
  firearms: <Firearms />,
  fur_leather: <Boot />,
  gambling: <Gambling />,
  gmo: <Genetics />,
  nuclear: <Nuclear />,
  pesticides: <Biohazard />,
  tobacco: <Tobacco />,
};

export const Exposure = ({ metrics }: SectorsProps) => {
  const { t } = useTranslation();
  const data = useMemo(
    () =>
      metrics
        .map(({ name, value }) => ({
          name: name.replace('_', ' '),
          value: +(value * 100).toFixed(2),
          icon: METRIC_ICONS?.[name as keyof typeof METRIC_ICONS] ?? null,
        }))
        .sort((x, y) => y.value - x.value),
    [metrics],
  );

  const maxValue = useMemo(
    () =>
      metrics.length > 0
        ? Math.max(...metrics.map(({ value }) => +(value * 100).toFixed(2)))
        : 100,
    [metrics],
  );

  return (
    <ChartContainer header={t('research.exposure', { ns: 'research' })}>
      <table className="w-full table-fixed" data-testid="esg-exposure-chart">
        <tbody>
          {Array.from(data, ({ name, value, icon }, key) => (
            <tr key={key}>
              <td className="w-[32px] pb-2 pr-4">
                <div className="flex flex-1 items-center justify-center [&>svg]:!min-h-[24px] [&>svg]:!min-w-[20px]">
                  {icon}
                </div>
              </td>
              <td className="min-w-[172px] pb-2 pr-5">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap text-left font-[poppins] capitalize leading-[24px] text-primary-medium">
                  {name}
                </span>
              </td>
              <td className="hidden min-w-[107px] pr-6 xl:table-cell">
                <progress className="progress" value={value} max={maxValue} />
              </td>
              <td className="w-16 pb-2 text-right">
                <span className="!money-display font-[poppins] leading-[24px] text-primary-medium">
                  {value}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ChartContainer>
  );
};
